import { Component, type ReactNode } from 'react';
import { reportBoundaryError } from '../errorReporter';

type Props = { children: ReactNode };
type State = { hasError: boolean; error?: Error };

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: { componentStack?: string | null }): void {
    // eslint-disable-next-line no-console
    console.error('[Cat vs AI] uncaught render error:', error, info);
    void reportBoundaryError(error.message, info.componentStack ?? error.stack);
  }

  private handleReset = (): void => {
    this.setState({ hasError: false, error: undefined });
  };

  private handleHardReset = (): void => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    const errorMessage = this.state.error?.message ?? 'Unknown failure.';
    const stack = this.state.error?.stack?.split('\n').slice(0, 3).join('\n') ?? '';

    return (
      <div
        className="scanlines"
        style={{
          position: 'fixed',
          inset: 0,
          background: 'var(--bg)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--space-4)',
          padding: 'var(--space-6)',
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + var(--space-6))',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + var(--space-6))',
          color: 'var(--fg-danger)',
          zIndex: 100,
        }}
      >
        <div className="scan-band" />

        <h1
          className="headline glow-danger pulse"
          style={{ fontSize: 'clamp(1.6rem, 6vw, 2.4rem)', textAlign: 'center' }}
        >
          // SYSTEM FAILURE
        </h1>

        <div
          className="terminal-frame"
          style={{
            maxWidth: '22rem',
            width: '100%',
            color: 'var(--fg-text-dim)',
            fontSize: '0.75rem',
          }}
        >
          <span className="corner-tr" />
          <span className="corner-bl" />
          <div className="label" style={{ color: 'var(--fg-danger)', marginBottom: 'var(--space-2)' }}>
            // CRASH REPORT
          </div>
          <p style={{ wordBreak: 'break-word', lineHeight: 1.5 }}>{errorMessage}</p>
          {stack && (
            <pre
              style={{
                marginTop: 'var(--space-2)',
                fontSize: '0.6rem',
                opacity: 0.5,
                whiteSpace: 'pre-wrap',
                overflow: 'hidden',
                color: 'var(--fg-muted)',
              }}
            >
              {stack}
            </pre>
          )}
        </div>

        <p
          style={{
            fontSize: '0.78rem',
            color: 'var(--fg-text-dim)',
            textAlign: 'center',
            maxWidth: '18rem',
            lineHeight: 1.4,
          }}
        >
          Bastet flagged this incident. Try recovering — if it crashes again, reload from scratch.
        </p>

        <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', justifyContent: 'center' }}>
          <button className="primary" onClick={this.handleReset}>
            ↺ RECOVER
          </button>
          <button className="danger" onClick={this.handleHardReset}>
            ⟳ HARD RESET
          </button>
        </div>
      </div>
    );
  }
}
